/*
 * Practice Settings saves with PATCH (KIN-1994), which changes only the settings the body
 * names and leaves every other one alone.
 *
 * It replaces a full-object POST that reset anything absent from the body to its model
 * default — which is how saving "Send calendar invites" switched laboratory ordering off for
 * a whole practice (KIN-1885). The old payload builder defended against that by sending the
 * loaded settings underneath the form's own values, so the fields the form has never heard of
 * were handed straight back. With PATCH there is nothing to defend: a field the body does not
 * name is not touched.
 *
 * So the body carries what the user actually changed, and nothing else. An unrecognised field
 * name is a 400 from the backend naming it, so the form must never send one — not `tenant`,
 * not `id`, neither of which is a setting.
 */

/*
 * The settings as the form holds them. The form's own `reset` reads the loaded settings
 * through these rules — a null boolean is a default, a null string is "" — so the diff has to
 * read them the same way. Comparing against the raw record instead would report a change on
 * every field the practice has never set, and the first visit to the page would patch a dozen
 * settings nobody touched.
 */
export const toPracticeSettingsFormValues = (settings) => ({
  practiceSenderEmailAddress: settings?.practiceSenderEmailAddress || "",
  canEmailReferrals: settings?.canEmailReferrals || false,
  allowOverlappingAppointments: settings?.allowOverlappingAppointments || false,
  allowDeleteAppointments: settings?.allowDeleteAppointments || false,
  sendCalendarInvites: settings?.sendCalendarInvites || false,
  showOpenConsultationsOnLogout:
    settings?.showOpenConsultationsOnLogout !== false,
  healthLink: settings?.healthLink || false,
  twoFactorAuthentication: settings?.twoFactorAuthentication !== false,
  consultMobile: settings?.consultMobile || false,
  medications: settings?.medications !== false,
  prescriptions: settings?.prescriptions !== false,
  sendEmail: settings?.sendEmail !== false,
  sickCert: settings?.sickCert !== false,
  letter: settings?.letter !== false,
  consultTypes: settings?.consultTypes || "",
  consultTimes: settings?.consultTimes || "",
  cancellationReasons: settings?.cancellationReasons || "",
  consultOutcomes: settings?.consultOutcomes || "",
  reasonForAbsence: settings?.reasonForAbsence || "",
  whatCantBeAttended: settings?.whatCantBeAttended || "",
  userRoles: settings?.userRoles || "",
});

/** The settings this form owns. Anything outside it is not the form's to send. */
export const PRACTICE_SETTINGS_FORM_FIELDS = Object.keys(
  toPracticeSettingsFormValues(null)
);

/**
 * What changed, as the PATCH body. Empty when nothing did, which the caller takes as nothing
 * to send — the backend would answer such a patch with the settings unchanged, but a request
 * that cannot change anything is not worth making.
 */
export const buildPracticeSettingsPatch = ({ settings, formValues }) => {
  const saved = toPracticeSettingsFormValues(settings);

  return PRACTICE_SETTINGS_FORM_FIELDS.reduce((patch, field) => {
    const value = formValues?.[field];

    if (value === undefined || value === saved[field]) return patch;

    return { ...patch, [field]: value };
  }, {});
};
